import React from 'react';
import { useSelector } from 'react-redux';
import { Card, CardContent } from '@client/components/ui/card';
import { Alert, AlertDescription } from '@client/components/ui/alert';
import { Skeleton } from '@client/components/ui/skeleton';
import { ContactSupportButton } from '@client/components/ContactSupportButton';
import { useAdminMode } from '@/hooks/useAdminMode';
import { AdminModeToggle } from '@/components/admin/AdminModeToggle';
import { AdminSearchBar } from '@/components/admin/AdminSearchBar';
import { SelectedAdvisorBar } from '@/components/admin/SelectedAdvisorBar';
import { OwnerInformationCard } from '@/components/admin/OwnerInformationCard';
import { selectAdvisorAdminPortalState, selectSession } from '@/model/selectors';
import {
  useSubscriptionSeats,
  SubscriptionOverviewCard,
  NonOwnerOverviewCard,
  SeatsTable,
  SeatDialogs,
} from '@client/components/advisor-account-seats';

function LoadingSkeleton() {
  return (
    <div className="flex-1">
      <main className="h-screen grow  px-4 pb-12 pt-3 sm:px-6 lg:px-8">
        <div className="my-7 flex flex-col">
          {/* Overview Card Skeleton */}
          <Card className="border-primary-200 mt-14 max-w-[900px] border">
            <CardContent className="flex flex-col gap-5 p-6">
              <div className="flex items-center justify-between">
                <Skeleton className="h-6 w-48 rounded" />
                <Skeleton className="h-6 w-20 rounded-full" />
              </div>
              <div className="flex flex-wrap gap-x-8 gap-y-4">
                <div className="min-w-[120px]">
                  <Skeleton className="mb-2 h-4 w-20 rounded" />
                  <Skeleton className="h-8 w-12 rounded" />
                </div>
                <div className="min-w-[120px]">
                  <Skeleton className="mb-2 h-4 w-16 rounded" />
                  <Skeleton className="h-8 w-24 rounded" />
                </div>
                <div className="min-w-[120px]">
                  <Skeleton className="mb-2 h-4 w-28 rounded" />
                  <Skeleton className="h-8 w-20 rounded" />
                </div>
                <div className="min-w-[120px]">
                  <Skeleton className="mb-2 h-4 w-28 rounded" />
                  <Skeleton className="h-8 w-40 rounded" />
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-3">
                <Skeleton className="h-10 w-28 rounded" />
                <Skeleton className="h-10 w-48 rounded" />
              </div>
            </CardContent>
          </Card>

          {/* Table Skeleton */}
          <Card className="border-primary-200 mt-6 max-w-[900px] border">
            <CardContent className="p-6">
              <Skeleton className="h-6 w-16 rounded" />
              <Skeleton className="mb-4 mt-1 h-4 w-64 rounded" />
              <div className="space-y-4">
                <div className="flex gap-4">
                  <Skeleton className="h-4 w-16 rounded" />
                  <Skeleton className="h-4 w-24 rounded" />
                  <Skeleton className="h-4 w-32 rounded" />
                  <Skeleton className="h-4 w-28 rounded" />
                  <Skeleton className="h-4 w-16 rounded" />
                </div>
                {[1, 2, 3].map(i => (
                  <div
                    key={i}
                    className="flex items-center gap-4"
                  >
                    <Skeleton className="h-6 w-16 rounded-full" />
                    <Skeleton className="h-4 w-24 rounded" />
                    <Skeleton className="h-4 w-48 rounded" />
                    <Skeleton className="h-4 w-24 rounded" />
                    <div className="flex gap-2">
                      <Skeleton className="h-8 w-8 rounded" />
                      <Skeleton className="h-8 w-8 rounded" />
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
}

function ErrorState({ error }: { error: string }) {
  return (
    <div className="flex-1">
      <main className="bg-primary-50 h-screen grow px-4 pb-12 pt-3 sm:px-6 lg:px-8">
        <div className="my-7 flex flex-col justify-between">
          <Alert
            variant="destructive"
            className="mt-14 max-w-[690px]"
          >
            <AlertDescription>
              <div className="space-y-4">
                <p>{error || 'Failed to load subscription data'}</p>
                <ContactSupportButton
                  subject="Subscription Data Load Error"
                  className="w-full sm:w-auto"
                />
              </div>
            </AlertDescription>
          </Alert>
        </div>
      </main>
    </div>
  );
}

function NoSubscriptionState() {
  return (
    <div className="flex-1">
      <main className="bg-primary-50 h-screen grow px-4 pb-12 pt-3 sm:px-6 lg:px-8">
        <div className="my-7 flex flex-col justify-between">
          <Alert className="mt-14 max-w-[690px]">
            <AlertDescription>
              <div className="space-y-4">
                <p>
                  No subscription found. If you believe this is an error, please contact support.
                </p>
                <ContactSupportButton
                  subject="No Subscription Data Found"
                  className="w-full sm:w-auto"
                />
              </div>
            </AlertDescription>
          </Alert>
        </div>
      </main>
    </div>
  );
}

interface AdvisorAccountSeatsProps {
  showAdvisorSearch?: boolean;
  adminMode?: boolean;
}

export function AdvisorAccountSeats({
  showAdvisorSearch: propShowAdvisorSearch = false,
  adminMode: propAdminMode = false,
}: AdvisorAccountSeatsProps = {}) {
  // Admin mode state and handlers
  const {
    isWizardUser,
    isAdminModeEnabled,
    selectedAdvisorId,
    adminModeActive,
    hasSelectedAdvisor,
    handleAdvisorSelected,
    handleToggleChange,
    handleChangeAdvisor,
  } = useAdminMode({
    propAdminMode,
    propShowAdvisorSearch,
  });

  // Get selected advisor info for display
  const { selectedAdvisor } = useSelector(selectAdvisorAdminPortalState);

  // Get session data for non-owner view
  const session = useSelector(selectSession);

  // In admin mode, use selectedAdvisorId; otherwise use null (defaults to session advisor)
  const targetAdvisorId = adminModeActive ? selectedAdvisorId : null;

  const {
    // Data
    seats,
    subscription,
    isOwner,
    creditBalance,
    unitPricePerSeat,
    activeSeatsCount,
    ownerInfo,
    currentAdvisorAssignedDateTime,

    // Fetch status
    isFetching,
    fetchError,

    // Mutation status
    isMutating,
    currentMutation,

    // Invitation preview
    invitationPreviewStatus,
    isPreviewingInvitation,
    invitationPreviewError,
    duplicateEmailError,
    invitationPreview,

    // Dialog state
    dialogType,
    emailInput,
    setEmailInput,
    showPreview,
    setShowPreview,

    // Actions
    openDialog,
    closeDialog,
    handlePreviewInvitation,
    handleSendInvitation,
    handleRemoveSeat,
    handleResendInvitation,
    handleDirectAssignSeat,
  } = useSubscriptionSeats(targetAdvisorId);

  // Show subscription management UI only when not in admin mode OR when advisor is selected
  const showSubscriptionUI =
    !adminModeActive || (adminModeActive && hasSelectedAdvisor && !isFetching && subscription);

  return (
    <div className="flex-1">
      <main className="bg-primary-50 h-screen grow px-4 pb-12 pt-3 sm:px-6 lg:px-8">
        <div className="my-7 flex flex-col">
          {/* Admin Controls - only show for wizards */}
          {isWizardUser && !propAdminMode && (
            <Card className="border-primary-200 max-w-[900px] border">
              <CardContent className="p-3">
                <div className="flex items-center gap-4">
                  <AdminModeToggle
                    enabled={isAdminModeEnabled}
                    onToggle={handleToggleChange}
                  />

                  {isAdminModeEnabled && (
                    <>
                      <div className="h-8 w-px bg-gray-300" />
                      {hasSelectedAdvisor && selectedAdvisor ? (
                        <SelectedAdvisorBar
                          advisor={selectedAdvisor}
                          onChangeAdvisor={handleChangeAdvisor}
                        />
                      ) : (
                        <AdminSearchBar onAdvisorSelected={handleAdvisorSelected} />
                      )}
                    </>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Loading State */}
          {(isFetching || isMutating) && <LoadingSkeleton />}

          {/* Error State */}
          {!isFetching && fetchError && <ErrorState error={fetchError} />}

          {/* No Subscription State */}
          {!isFetching && !fetchError && adminModeActive && hasSelectedAdvisor && !subscription && (
            <NoSubscriptionState />
          )}

          {/* Subscription Management UI */}
          {showSubscriptionUI && subscription && (
            <>
              {/* Show owner info card if viewing a non-owner's subscription in admin mode */}
              {adminModeActive && !isOwner && ownerInfo && subscription.ownerAdvisorId && (
                <div className="mt-4">
                  <OwnerInformationCard
                    ownerInfo={{
                      advisorId: subscription.ownerAdvisorId,
                      advisorName: ownerInfo.advisorName,
                      advisorEmail: ownerInfo.advisorEmail || null,
                      organization: ownerInfo.organization,
                    }}
                  />
                </div>
              )}

              {/* Show management view ONLY for actual owners (even in admin mode) */}
              {isOwner ? (
                <div className="space-y-4">
                  <SubscriptionOverviewCard
                    subscription={subscription}
                    activeSeatsCount={activeSeatsCount}
                    creditBalance={creditBalance}
                    unitPricePerSeat={unitPricePerSeat}
                    isOwner={isOwner}
                    onAddSeat={() => openDialog('addSeat')}
                  />

                  {/* Hide seats table if subscription is canceled or unpaid */}
                  {subscription?.subscriptionStatus !== 'canceled' &&
                    subscription?.subscriptionStatus !== 'unpaid' && (
                      <SeatsTable
                        seats={seats}
                        subscription={subscription}
                        isOwner={isOwner}
                        isMutating={isMutating}
                        currentMutation={currentMutation}
                        onRemoveSeat={(seatId: string) => openDialog('removeSeat', seatId)}
                        onResendInvitation={handleResendInvitation}
                      />
                    )}
                </div>
              ) : (
                /* Non-Owner View - shows for both normal users and admins viewing non-owners */
                <NonOwnerOverviewCard
                  ownerInfo={ownerInfo}
                  currentAdvisorAssignedDateTime={currentAdvisorAssignedDateTime}
                  seatStatus={session.stripe?.seatStatus}
                  sessionType={session.sessionType}
                  subscription={subscription}
                />
              )}
            </>
          )}
        </div>
      </main>

      {/* Seat Management Dialogs (only show for actual owners) */}
      {isOwner && (
        <SeatDialogs
          dialogType={dialogType}
          emailInput={emailInput}
          setEmailInput={setEmailInput}
          showPreview={showPreview}
          setShowPreview={setShowPreview}
          invitationPreviewStatus={invitationPreviewStatus}
          isPreviewingInvitation={isPreviewingInvitation}
          invitationPreviewError={invitationPreviewError}
          duplicateEmailError={duplicateEmailError}
          invitationPreview={invitationPreview}
          isMutating={isMutating}
          currentMutation={currentMutation}
          isWizardUser={isWizardUser}
          onClose={closeDialog}
          onPreviewInvitation={handlePreviewInvitation}
          onSendInvitation={handleSendInvitation}
          onRemoveSeat={handleRemoveSeat}
          onDirectAssignSeat={handleDirectAssignSeat}
        />
      )}
    </div>
  );
}
